import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, BarChart3, FileCheck, Shield } from 'lucide-react';

export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>
      
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <div className="text-center lg:text-left">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
              Décidez plus vite quels projets de reprise{' '}
              <span className="text-accent">méritent votre temps</span>.
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
              RepriScan est l'outil de prédiagnostic qui analyse un dossier en quelques minutes et vous donne un verdict clair.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Link to="/auth">
                <Button size="lg" className="w-full sm:w-auto gap-2">
                  Commencer mon premier scan
                  <span className="text-xs opacity-80">(1 gratuit)</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              
              <Link to="/pricing">
                <Button variant="outline" size="lg" className="w-full sm:w-auto">
                  Voir les plans
                </Button>
              </Link>
            </div>
            
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Shield className="h-4 w-4 text-verdict-green" />
                Données sécurisées
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-verdict-green" />
                Sans engagement
              </span>
            </div>
          </div>
          
          {/* Right: Visual mockup */}
          <div className="relative">
            <div className="bg-card rounded-2xl shadow-xl border overflow-hidden">
              {/* Mock dashboard header */}
              <div className="bg-primary px-4 py-3 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-primary-foreground/30" />
                <div className="w-3 h-3 rounded-full bg-primary-foreground/30" />
                <div className="w-3 h-3 rounded-full bg-primary-foreground/30" />
              </div>
              
              {/* Mock synthesis content */}
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Synthèse Décisionnelle</p>
                    <p className="font-semibold">Boulangerie Martin</p>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-verdict-green-bg text-verdict-green text-sm font-medium">
                    <CheckCircle2 className="h-4 w-4" />
                    Poursuivre
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <BarChart3 className="h-5 w-5 mx-auto mb-1 text-accent" />
                    <p className="text-2xl font-bold">78</p>
                    <p className="text-xs text-muted-foreground">Score global</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <FileCheck className="h-5 w-5 mx-auto mb-1 text-accent" />
                    <p className="text-2xl font-bold">Bon</p>
                    <p className="text-xs text-muted-foreground">Fit repreneur</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <Shield className="h-5 w-5 mx-auto mb-1 text-verdict-green" />
                    <p className="text-2xl font-bold">0</p>
                    <p className="text-xs text-muted-foreground">Red flags</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Points forts identifiés</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-verdict-green-bg text-verdict-green text-xs rounded">
                      Rentabilité stable
                    </span>
                    <span className="px-2 py-1 bg-verdict-green-bg text-verdict-green text-xs rounded">
                      Bon emplacement
                    </span>
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                      + 3 autres
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 bg-card rounded-lg shadow-lg border px-4 py-2">
              <p className="text-xs text-muted-foreground">Analyse complète en</p>
              <p className="text-lg font-bold text-accent">~3 minutes</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
